import { Component, OnInit } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms';
import { ParametersService } from '@modules/business-accounts/services/parameters/parameters.service';
import { NullableObservable } from '@modules/business-accounts/utils/types/observable.type';
import { Unsubscriber } from '@utils/mixins/unsubscribe.mixin';
import { Observable, of, startWith, switchMap, tap } from 'rxjs';
import { dynamicAddressValidator } from './address.form';

@Component({
  selector: 'ba-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class AddressFormComponent extends Unsubscriber() implements OnInit {
  department$!: Observable<any>;
  provinces$!: NullableObservable<ReturnType<ParametersService['getProvinces']>>;
  districts$!: NullableObservable<ReturnType<ParametersService['getDistricts']>>;

  list = {
    departments: [],
    provinces: [],
    districts: [],
  };

  constructor(private readonly controlContainer: ControlContainer, public readonly paramsService: ParametersService) {
    super();
  }

  ngOnInit(): void {
    this.department$ = this.paramsService.params$('departments').pipe(
      tap((res) => {
        this.list.departments = res as any;
      })
    );
    this.applyDynamicAddressValidator();
    this.listenToDepartmentChange();
    this.listenToProvinceChange();
    this.listenToDistrictChange();
  }

  get form() {
    return this.controlContainer.control as FormGroup;
  }

  applyDynamicAddressValidator() {
    this.subsink.sink = this.form.valueChanges.subscribe(() => dynamicAddressValidator({ on: this.form }));
  }

  listenToDepartmentChange() {
    let { department } = this.form.value;

    this.provinces$ = this.form.get('department')!.valueChanges.pipe(
      startWith(this.form.get('department')?.value),
      switchMap((res) => {
        const departmentName: any = this.list.departments.find((d: any) => d.value === res);
        if (departmentName) {
          this.form.get('departmentName')?.setValue(departmentName.label);
        }
        return res ? this.paramsService.getProvinces(res) : of(null);
      }),
      tap((res) => {
        this.list.provinces = res as any;
        if (!department) {
          this.form.get('province')?.setValue(null);
          this.form.get('district')?.setValue(null);
          this.form.get('provinceName')?.setValue(null);
          this.form.get('districtName')?.setValue(null);
        } else {
          department = null;
        }
      })
    );
  }

  listenToProvinceChange() {
    let { department, province } = this.form.value;

    this.districts$ = this.form.get('province')!.valueChanges.pipe(
      startWith(this.form.get('province')?.value),
      switchMap((res) => {
        if (!res) return of(null);
        const { department } = this.form.value;
        const provinceName: any = this.list.provinces?.find((d: any) => d.value === res);

        if (provinceName) this.form.get('provinceName')?.setValue(provinceName.label);
        return this.paramsService.getDistricts(department, res);
      }),
      tap((res) => {
        this.list.districts = res as any;
        if (!department && !province) {
          this.form.get('district')?.setValue(null);
        } else {
          department = null;
          province = null;
        }
      })
    );
  }

  listenToDistrictChange() {
    this.form.get('district')?.valueChanges.subscribe((res) => {
      const districtName: any = this.list.districts?.find((d: any) => d.value === res);
      if (districtName) this.form.get('districtName')?.setValue(districtName.label);
    });
  }
}
